import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Copy } from "lucide-react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useTranslation } from "@/packages/utils";
import type { ChatMessage } from "@/components/ui/ChatPanel";

interface ChatMessageBubbleProps {
  message: ChatMessage;
  index: number;
  isStreaming?: boolean;
}

function CopyButton({ content }: { content: string }) {
  const { t } = useTranslation(["documents"]);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="absolute top-2 right-2 p-1.5 rounded-md bg-surface/80 backdrop-blur-sm border border-border/50 text-muted hover:text-primary hover:bg-surface transition-all opacity-0 group-hover:opacity-100"
      aria-label={t("documents:chat.copyMessage")}
    >
      {copied ? (
        <Check className="h-3.5 w-3.5 text-emerald-500" />
      ) : (
        <Copy className="h-3.5 w-3.5" />
      )}
    </button>
  );
}

export function ChatMessageBubble({ message, index }: ChatMessageBubbleProps) {
  const isUser = message.role === "user";

  return (
    <motion.div
      className={`flex flex-col ${isUser ? "items-end" : "items-start"}`}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, delay: index * 0.03, ease: "easeOut" }}
    >
      {isUser ? (
        <div className="bg-accent text-white rounded-2xl rounded-br-sm px-4 py-2.5 max-w-[85%]">
          <p className="text-sm whitespace-pre-wrap leading-relaxed">
            {message.content}
          </p>
        </div>
      ) : (
        <div className="group relative bg-card text-primary rounded-2xl rounded-bl-sm px-4 py-2.5 max-w-[85%]">
          <div className="prose-chat text-sm leading-relaxed">
            <Markdown remarkPlugins={[remarkGfm]}>{message.content}</Markdown>
          </div>
        </div>
      )}
      <CopyButton content={message.content} />

      {message.timestamp && (
        <span className="text-[11px] text-muted mt-1 px-1">
          {formatTimestamp(message.timestamp)}
        </span>
      )}
    </motion.div>
  );
}

function formatTimestamp(timestamp: string): string {
  try {
    const date = new Date(timestamp);
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return "";
  }
}
